// The one place `localStorage` is touched for client-only preferences
// (`frontend-restructuring/00-guardrails.md` §0.2c). Everything else — containers, shell,
// hooks — reads/writes through here; presentational components never persist anything
// (`eslint.config.js` `no-restricted-syntax`).
//
// Why a wrapper:
//  - `getItem`/`setItem` **throw** in private browsing (Safari/Firefox), when the quota is
//    hit, or when site data is disabled — every access must be guarded.
//  - Keys are **versioned** (`<key>:v<n>`) so a shape change is a version bump + a one-time
//    `migrateLegacyKey`, never a silent read of stale JSON.
//  - Values are JSON, so callers store plain data, not strings.
//
// NOT for: auth (`token`, `currentUser` — session-critical, handled by the auth layer) or
// anything that mirrors the server (`/api/customs` keys like `task_table_column_widths` —
// those are React Query state, see §0.8b).

const PREFIX = 'colonny'

const namespaced = (key, version) => `${PREFIX}:${key}:v${version}`

/** Read a versioned pref. Returns `fallback` on any error, missing key, or bad JSON. */
export function readStore(key, { version = 1, fallback = null } = {}) {
  try {
    const raw = localStorage.getItem(namespaced(key, version))
    if (raw == null) return fallback
    return JSON.parse(raw)
  } catch {
    return fallback
  }
}

/** Write a versioned pref. Returns `false` if storage is unavailable / full. */
export function writeStore(key, value, { version = 1 } = {}) {
  try {
    localStorage.setItem(namespaced(key, version), JSON.stringify(value))
    return true
  } catch {
    return false
  }
}

/** Remove a versioned pref. */
export function removeStore(key, { version = 1 } = {}) {
  try {
    localStorage.removeItem(namespaced(key, version))
  } catch {
    /* storage unavailable — nothing to remove */
  }
}

/**
 * One-time migration of a raw pre-wrapper key (e.g. `colonny_control_menu_collapsed`) into the
 * versioned store. Reads the old value once, writes it through `writeStore` after `transform`,
 * then deletes the old key. Safe to call on every load — no-op once the old key is gone.
 */
export function migrateLegacyKey(oldKey, newKey, { version = 1, transform = (raw) => raw } = {}) {
  try {
    const raw = localStorage.getItem(oldKey)
    if (raw == null) return
    writeStore(newKey, transform(raw), { version })
    localStorage.removeItem(oldKey)
  } catch {
    /* storage unavailable — the old key (if any) stays; next load retries */
  }
}
